import { useCallback, useEffect, useMemo, useState } from 'react';
import { markSeen, readSeen } from './contact-seen';

/**
 * The React half of the Outreach screen's NEW dot: reads a person's seen-mark, stamps one, and re-renders
 * every reader when a mark changes (one module-level generation, so the tile count, the list and the
 * detail agree without a store). `seenAt` answers `null` until the first client effect — the server has
 * no storage, and a value computed during render would trip hydration.
 */
let generation = 0;
const listeners = new Set<() => void>();
function bump() {
  generation += 1;
  listeners.forEach((l) => l());
}

export interface ContactSeen {
  seenAt: (personId: string | undefined) => number | null;
  mark: (personId: string) => void;
  markAll: (personIds: string[]) => void;
  ready: boolean;
  /** Bumps on every mark, so a memo keyed on this object recomputes. */
  generation: number;
}

export function useContactSeen(): ContactSeen {
  const [tick, setTick] = useState(0);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(true);
    const l = () => setTick((n) => n + 1);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);
  const seenAt = useCallback((personId: string | undefined) => (ready && personId ? readSeen(personId) : null), [ready]);
  const mark = useCallback((personId: string) => {
    markSeen(personId);
    bump();
  }, []);
  const markAll = useCallback((personIds: string[]) => {
    const at = Date.now();
    personIds.forEach((id) => markSeen(id, at));
    bump();
  }, []);
  // `tick` is a dependency so a mark re-creates the object and dependents recompute.
  return useMemo(() => ({ seenAt, mark, markAll, ready, generation: tick }), [seenAt, mark, markAll, ready, tick]);
}
